/**
 * OWL2 Turtle Serializer
 *
 * Serializes OWL2 Ontology to Turtle (Terse RDF Triple Language) format.
 * Uses N3.js for RDF serialization.
 */
import { Writer, DataFactory } from 'n3';
import { iriToString } from '../types/IRI';
import { OWL_NAMESPACE, RDF_NAMESPACE, RDFS_NAMESPACE, XSD_NAMESPACE } from '../xml/constants';
const { namedNode, blankNode, literal, quad } = DataFactory;
// Common RDF/OWL terms
const RDF = {
    type: namedNode(`${RDF_NAMESPACE}type`),
    first: namedNode(`${RDF_NAMESPACE}first`),
    rest: namedNode(`${RDF_NAMESPACE}rest`),
    nil: namedNode(`${RDF_NAMESPACE}nil`),
};
const RDFS = {
    subClassOf: namedNode(`${RDFS_NAMESPACE}subClassOf`),
    subPropertyOf: namedNode(`${RDFS_NAMESPACE}subPropertyOf`),
    domain: namedNode(`${RDFS_NAMESPACE}domain`),
    range: namedNode(`${RDFS_NAMESPACE}range`),
    label: namedNode(`${RDFS_NAMESPACE}label`),
    comment: namedNode(`${RDFS_NAMESPACE}comment`),
};
const OWL = {
    Ontology: namedNode(`${OWL_NAMESPACE}Ontology`),
    Class: namedNode(`${OWL_NAMESPACE}Class`),
    ObjectProperty: namedNode(`${OWL_NAMESPACE}ObjectProperty`),
    DatatypeProperty: namedNode(`${OWL_NAMESPACE}DatatypeProperty`),
    AnnotationProperty: namedNode(`${OWL_NAMESPACE}AnnotationProperty`),
    NamedIndividual: namedNode(`${OWL_NAMESPACE}NamedIndividual`),
    Datatype: namedNode(`${OWL_NAMESPACE}Datatype`),
    Restriction: namedNode(`${OWL_NAMESPACE}Restriction`),
    Thing: namedNode(`${OWL_NAMESPACE}Thing`),
    Nothing: namedNode(`${OWL_NAMESPACE}Nothing`),
    equivalentClass: namedNode(`${OWL_NAMESPACE}equivalentClass`),
    disjointWith: namedNode(`${OWL_NAMESPACE}disjointWith`),
    disjointUnionOf: namedNode(`${OWL_NAMESPACE}disjointUnionOf`),
    AllDisjointClasses: namedNode(`${OWL_NAMESPACE}AllDisjointClasses`),
    members: namedNode(`${OWL_NAMESPACE}members`),
    equivalentProperty: namedNode(`${OWL_NAMESPACE}equivalentProperty`),
    propertyDisjointWith: namedNode(`${OWL_NAMESPACE}propertyDisjointWith`),
    AllDisjointProperties: namedNode(`${OWL_NAMESPACE}AllDisjointProperties`),
    inverseOf: namedNode(`${OWL_NAMESPACE}inverseOf`),
    FunctionalProperty: namedNode(`${OWL_NAMESPACE}FunctionalProperty`),
    InverseFunctionalProperty: namedNode(`${OWL_NAMESPACE}InverseFunctionalProperty`),
    ReflexiveProperty: namedNode(`${OWL_NAMESPACE}ReflexiveProperty`),
    IrreflexiveProperty: namedNode(`${OWL_NAMESPACE}IrreflexiveProperty`),
    SymmetricProperty: namedNode(`${OWL_NAMESPACE}SymmetricProperty`),
    AsymmetricProperty: namedNode(`${OWL_NAMESPACE}AsymmetricProperty`),
    TransitiveProperty: namedNode(`${OWL_NAMESPACE}TransitiveProperty`),
    onProperty: namedNode(`${OWL_NAMESPACE}onProperty`),
    someValuesFrom: namedNode(`${OWL_NAMESPACE}someValuesFrom`),
    allValuesFrom: namedNode(`${OWL_NAMESPACE}allValuesFrom`),
    hasValue: namedNode(`${OWL_NAMESPACE}hasValue`),
    hasSelf: namedNode(`${OWL_NAMESPACE}hasSelf`),
    minCardinality: namedNode(`${OWL_NAMESPACE}minCardinality`),
    maxCardinality: namedNode(`${OWL_NAMESPACE}maxCardinality`),
    cardinality: namedNode(`${OWL_NAMESPACE}cardinality`),
    minQualifiedCardinality: namedNode(`${OWL_NAMESPACE}minQualifiedCardinality`),
    maxQualifiedCardinality: namedNode(`${OWL_NAMESPACE}maxQualifiedCardinality`),
    qualifiedCardinality: namedNode(`${OWL_NAMESPACE}qualifiedCardinality`),
    onClass: namedNode(`${OWL_NAMESPACE}onClass`),
    onDataRange: namedNode(`${OWL_NAMESPACE}onDataRange`),
    intersectionOf: namedNode(`${OWL_NAMESPACE}intersectionOf`),
    unionOf: namedNode(`${OWL_NAMESPACE}unionOf`),
    complementOf: namedNode(`${OWL_NAMESPACE}complementOf`),
    oneOf: namedNode(`${OWL_NAMESPACE}oneOf`),
    datatypeComplementOf: namedNode(`${OWL_NAMESPACE}datatypeComplementOf`),
    onDatatype: namedNode(`${OWL_NAMESPACE}onDatatype`),
    withRestrictions: namedNode(`${OWL_NAMESPACE}withRestrictions`),
    propertyChainAxiom: namedNode(`${OWL_NAMESPACE}propertyChainAxiom`),
    sameAs: namedNode(`${OWL_NAMESPACE}sameAs`),
    differentFrom: namedNode(`${OWL_NAMESPACE}differentFrom`),
    AllDifferent: namedNode(`${OWL_NAMESPACE}AllDifferent`),
    distinctMembers: namedNode(`${OWL_NAMESPACE}distinctMembers`),
    imports: namedNode(`${OWL_NAMESPACE}imports`),
    versionIRI: namedNode(`${OWL_NAMESPACE}versionIRI`),
    NegativePropertyAssertion: namedNode(`${OWL_NAMESPACE}NegativePropertyAssertion`),
    sourceIndividual: namedNode(`${OWL_NAMESPACE}sourceIndividual`),
    assertionProperty: namedNode(`${OWL_NAMESPACE}assertionProperty`),
    targetIndividual: namedNode(`${OWL_NAMESPACE}targetIndividual`),
    targetValue: namedNode(`${OWL_NAMESPACE}targetValue`),
    hasKey: namedNode(`${OWL_NAMESPACE}hasKey`),
    annotatedSource: namedNode(`${OWL_NAMESPACE}annotatedSource`),
    annotatedProperty: namedNode(`${OWL_NAMESPACE}annotatedProperty`),
    annotatedTarget: namedNode(`${OWL_NAMESPACE}annotatedTarget`),
    Axiom: namedNode(`${OWL_NAMESPACE}Axiom`),
};
/**
 * Serializes an OWL2 Ontology to Turtle format.
 */
export class Owl2TurtleSerializer {
    constructor() {
        Object.defineProperty(this, "quads", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "blankNodeCounter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "prefixes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
    }
    /**
     * Serialize an Ontology to Turtle string.
     */
    serialize(ontology) {
        this.quads = [];
        this.blankNodeCounter = 0;
        this.prefixes = new Map();
        // Collect prefixes
        for (const prefix of ontology.prefixes) {
            this.prefixes.set(prefix.name, prefix.iri);
        }
        // Add standard prefixes if not present
        if (!this.prefixes.has('owl'))
            this.prefixes.set('owl', OWL_NAMESPACE);
        if (!this.prefixes.has('rdf'))
            this.prefixes.set('rdf', RDF_NAMESPACE);
        if (!this.prefixes.has('rdfs'))
            this.prefixes.set('rdfs', RDFS_NAMESPACE);
        if (!this.prefixes.has('xsd'))
            this.prefixes.set('xsd', XSD_NAMESPACE);
        // Serialize ontology header
        this.serializeOntologyHeader(ontology);
        // Serialize axioms
        for (const axiom of ontology.axioms) {
            this.serializeAxiom(axiom);
        }
        // Write to Turtle
        const writer = new Writer({
            prefixes: Object.fromEntries(this.prefixes)
        });
        for (const q of this.quads) {
            writer.addQuad(q);
        }
        let result = '';
        writer.end((error, output) => {
            if (error)
                throw error;
            result = output;
        });
        return result;
    }
    newBlankNode() {
        return blankNode(`b${this.blankNodeCounter++}`);
    }
    resolveIRI(iri) {
        if (iri.kind === 'FullIRI') {
            return namedNode(iri.value);
        }
        // Abbreviated IRI
        const namespace = this.prefixes.get(iri.prefix);
        if (namespace) {
            return namedNode(namespace + iri.localName);
        }
        // Fallback: use as-is
        return namedNode(`${iri.prefix}:${iri.localName}`);
    }
    serializeOntologyHeader(ontology) {
        if (ontology.ontologyIRI) {
            const ontoNode = this.resolveIRI(ontology.ontologyIRI);
            this.quads.push(quad(ontoNode, RDF.type, OWL.Ontology));
            if (ontology.versionIRI) {
                this.quads.push(quad(ontoNode, OWL.versionIRI, this.resolveIRI(ontology.versionIRI)));
            }
            for (const imp of ontology.imports) {
                this.quads.push(quad(ontoNode, OWL.imports, this.resolveIRI(imp.iri)));
            }
            // Ontology annotations
            for (const ann of ontology.annotations) {
                this.serializeAnnotation(ontoNode, ann);
            }
        }
    }
    serializeAnnotation(subject, annotation) {
        const property = this.resolveIRI(annotation.property.iri);
        const value = this.serializeAnnotationValue(annotation.value);
        this.quads.push(quad(subject, property, value));
    }
    serializeAnnotationValue(value) {
        // Check for AnonymousIndividual first (has 'type' property)
        if ('type' in value && value.type === 'AnonymousIndividual') {
            return blankNode(value.nodeId);
        }
        // Check for IRI (has 'kind' property with FullIRI or AbbreviatedIRI)
        if ('kind' in value && (value.kind === 'FullIRI' || value.kind === 'AbbreviatedIRI')) {
            return this.resolveIRI(value);
        }
        // Must be Literal (has 'lexicalForm' and 'kind' with TypedLiteral or PlainLiteral)
        if ('lexicalForm' in value) {
            return this.serializeLiteral(value);
        }
        throw new Error('Unknown annotation value type');
    }
    serializeLiteral(lit) {
        if (lit.kind === 'PlainLiteral') {
            if (lit.languageTag) {
                return literal(lit.lexicalForm, lit.languageTag);
            }
            return literal(lit.lexicalForm);
        }
        // TypedLiteral
        const datatypeIRI = iriToString(lit.datatypeIRI);
        return literal(lit.lexicalForm, namedNode(datatypeIRI));
    }
    serializeAxiom(axiom) {
        switch (axiom.axiomType) {
            case 'Declaration':
                this.serializeDeclaration(axiom);
                break;
            case 'SubClassOf':
                this.serializeSubClassOf(axiom);
                break;
            case 'EquivalentClasses':
                this.serializeEquivalentClasses(axiom);
                break;
            case 'DisjointClasses':
                this.serializeDisjointClasses(axiom);
                break;
            case 'DisjointUnion':
                this.serializeDisjointUnion(axiom);
                break;
            case 'SubObjectPropertyOf':
                this.serializeSubObjectPropertyOf(axiom);
                break;
            case 'EquivalentObjectProperties':
                this.serializeEquivalentObjectProperties(axiom);
                break;
            case 'DisjointObjectProperties':
                this.serializeDisjointObjectProperties(axiom);
                break;
            case 'InverseObjectProperties':
                this.serializeInverseObjectProperties(axiom);
                break;
            case 'ObjectPropertyDomain':
                this.serializeObjectPropertyDomain(axiom);
                break;
            case 'ObjectPropertyRange':
                this.serializeObjectPropertyRange(axiom);
                break;
            case 'FunctionalObjectProperty':
                this.serializeFunctionalObjectProperty(axiom);
                break;
            case 'InverseFunctionalObjectProperty':
                this.serializeInverseFunctionalObjectProperty(axiom);
                break;
            case 'ReflexiveObjectProperty':
                this.serializeReflexiveObjectProperty(axiom);
                break;
            case 'IrreflexiveObjectProperty':
                this.serializeIrreflexiveObjectProperty(axiom);
                break;
            case 'SymmetricObjectProperty':
                this.serializeSymmetricObjectProperty(axiom);
                break;
            case 'AsymmetricObjectProperty':
                this.serializeAsymmetricObjectProperty(axiom);
                break;
            case 'TransitiveObjectProperty':
                this.serializeTransitiveObjectProperty(axiom);
                break;
            case 'SubDataPropertyOf':
                this.serializeSubDataPropertyOf(axiom);
                break;
            case 'EquivalentDataProperties':
                this.serializeEquivalentDataProperties(axiom);
                break;
            case 'DisjointDataProperties':
                this.serializeDisjointDataProperties(axiom);
                break;
            case 'DataPropertyDomain':
                this.serializeDataPropertyDomain(axiom);
                break;
            case 'DataPropertyRange':
                this.serializeDataPropertyRange(axiom);
                break;
            case 'FunctionalDataProperty':
                this.serializeFunctionalDataProperty(axiom);
                break;
            case 'DatatypeDefinition':
                this.serializeDatatypeDefinition(axiom);
                break;
            case 'HasKey':
                this.serializeHasKey(axiom);
                break;
            case 'SameIndividual':
                this.serializeSameIndividual(axiom);
                break;
            case 'DifferentIndividuals':
                this.serializeDifferentIndividuals(axiom);
                break;
            case 'ClassAssertion':
                this.serializeClassAssertion(axiom);
                break;
            case 'ObjectPropertyAssertion':
                this.serializeObjectPropertyAssertion(axiom);
                break;
            case 'NegativeObjectPropertyAssertion':
                this.serializeNegativeObjectPropertyAssertion(axiom);
                break;
            case 'DataPropertyAssertion':
                this.serializeDataPropertyAssertion(axiom);
                break;
            case 'NegativeDataPropertyAssertion':
                this.serializeNegativeDataPropertyAssertion(axiom);
                break;
            case 'AnnotationAssertion':
                this.serializeAnnotationAssertion(axiom);
                break;
            case 'SubAnnotationPropertyOf':
                this.serializeSubAnnotationPropertyOf(axiom);
                break;
            case 'AnnotationPropertyDomain':
                this.serializeAnnotationPropertyDomain(axiom);
                break;
            case 'AnnotationPropertyRange':
                this.serializeAnnotationPropertyRange(axiom);
                break;
        }
    }
    // =========================================================================
    // Declaration Serialization
    // =========================================================================
    serializeDeclaration(axiom) {
        const entity = axiom.entity;
        const entityNode = this.resolveIRI(entity.iri);
        switch (entity.entityType) {
            case 'Class':
                this.quads.push(quad(entityNode, RDF.type, OWL.Class));
                break;
            case 'Datatype':
                this.quads.push(quad(entityNode, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                break;
            case 'ObjectProperty':
                this.quads.push(quad(entityNode, RDF.type, OWL.ObjectProperty));
                break;
            case 'DataProperty':
                this.quads.push(quad(entityNode, RDF.type, OWL.DatatypeProperty));
                break;
            case 'AnnotationProperty':
                this.quads.push(quad(entityNode, RDF.type, OWL.AnnotationProperty));
                break;
            case 'NamedIndividual':
                this.quads.push(quad(entityNode, RDF.type, OWL.NamedIndividual));
                break;
        }
    }
    // =========================================================================
    // Class Axiom Serialization
    // =========================================================================
    serializeSubClassOf(axiom) {
        const subNode = this.serializeClassExpression(axiom.subClass);
        const superNode = this.serializeClassExpression(axiom.superClass);
        this.quads.push(quad(subNode, RDFS.subClassOf, superNode));
    }
    serializeEquivalentClasses(axiom) {
        const nodes = axiom.classExpressions.map(ce => this.serializeClassExpression(ce));
        // Pairwise equivalence
        for (let i = 0; i < nodes.length - 1; i++) {
            this.quads.push(quad(nodes[i], OWL.equivalentClass, nodes[i + 1]));
        }
    }
    serializeDisjointClasses(axiom) {
        if (axiom.classExpressions.length === 2) {
            // Binary disjointness
            const node1 = this.serializeClassExpression(axiom.classExpressions[0]);
            const node2 = this.serializeClassExpression(axiom.classExpressions[1]);
            this.quads.push(quad(node1, OWL.disjointWith, node2));
        }
        else {
            // N-ary disjointness using AllDisjointClasses
            const bn = this.newBlankNode();
            this.quads.push(quad(bn, RDF.type, OWL.AllDisjointClasses));
            const listNode = this.serializeList(axiom.classExpressions.map(ce => this.serializeClassExpression(ce)));
            this.quads.push(quad(bn, OWL.members, listNode));
        }
    }
    serializeDisjointUnion(axiom) {
        const classNode = this.resolveIRI(axiom.disjointClass.iri);
        const listNode = this.serializeList(axiom.classExpressions.map(ce => this.serializeClassExpression(ce)));
        this.quads.push(quad(classNode, OWL.disjointUnionOf, listNode));
    }
    // =========================================================================
    // Object Property Axiom Serialization
    // =========================================================================
    serializeSubObjectPropertyOf(axiom) {
        const superNode = this.serializeObjectPropertyExpression(axiom.superProperty);
        if ('type' in axiom.subProperty && axiom.subProperty.type === 'ObjectPropertyChain') {
            // Property chain
            const chainList = this.serializeList(axiom.subProperty.properties.map(p => this.serializeObjectPropertyExpression(p)));
            this.quads.push(quad(superNode, OWL.propertyChainAxiom, chainList));
        }
        else {
            const subNode = this.serializeObjectPropertyExpression(axiom.subProperty);
            this.quads.push(quad(subNode, RDFS.subPropertyOf, superNode));
        }
    }
    serializeEquivalentObjectProperties(axiom) {
        const nodes = axiom.properties.map(p => this.serializeObjectPropertyExpression(p));
        for (let i = 0; i < nodes.length - 1; i++) {
            this.quads.push(quad(nodes[i], OWL.equivalentProperty, nodes[i + 1]));
        }
    }
    serializeDisjointObjectProperties(axiom) {
        if (axiom.properties.length === 2) {
            const node1 = this.serializeObjectPropertyExpression(axiom.properties[0]);
            const node2 = this.serializeObjectPropertyExpression(axiom.properties[1]);
            this.quads.push(quad(node1, OWL.propertyDisjointWith, node2));
        }
        else {
            const bn = this.newBlankNode();
            this.quads.push(quad(bn, RDF.type, OWL.AllDisjointProperties));
            const listNode = this.serializeList(axiom.properties.map(p => this.serializeObjectPropertyExpression(p)));
            this.quads.push(quad(bn, OWL.members, listNode));
        }
    }
    serializeInverseObjectProperties(axiom) {
        const node1 = this.serializeObjectPropertyExpression(axiom.property1);
        const node2 = this.serializeObjectPropertyExpression(axiom.property2);
        this.quads.push(quad(node1, OWL.inverseOf, node2));
    }
    serializeObjectPropertyDomain(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        const domainNode = this.serializeClassExpression(axiom.domain);
        this.quads.push(quad(propNode, RDFS.domain, domainNode));
    }
    serializeObjectPropertyRange(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        const rangeNode = this.serializeClassExpression(axiom.range);
        this.quads.push(quad(propNode, RDFS.range, rangeNode));
    }
    serializeFunctionalObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.FunctionalProperty));
    }
    serializeInverseFunctionalObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.InverseFunctionalProperty));
    }
    serializeReflexiveObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.ReflexiveProperty));
    }
    serializeIrreflexiveObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.IrreflexiveProperty));
    }
    serializeSymmetricObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.SymmetricProperty));
    }
    serializeAsymmetricObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.AsymmetricProperty));
    }
    serializeTransitiveObjectProperty(axiom) {
        const propNode = this.serializeObjectPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.TransitiveProperty));
    }
    // =========================================================================
    // Data Property Axiom Serialization
    // =========================================================================
    serializeSubDataPropertyOf(axiom) {
        const subNode = this.serializeDataPropertyExpression(axiom.subProperty);
        const superNode = this.serializeDataPropertyExpression(axiom.superProperty);
        this.quads.push(quad(subNode, RDFS.subPropertyOf, superNode));
    }
    serializeEquivalentDataProperties(axiom) {
        const nodes = axiom.properties.map(p => this.serializeDataPropertyExpression(p));
        for (let i = 0; i < nodes.length - 1; i++) {
            this.quads.push(quad(nodes[i], OWL.equivalentProperty, nodes[i + 1]));
        }
    }
    serializeDisjointDataProperties(axiom) {
        if (axiom.properties.length === 2) {
            const node1 = this.serializeDataPropertyExpression(axiom.properties[0]);
            const node2 = this.serializeDataPropertyExpression(axiom.properties[1]);
            this.quads.push(quad(node1, OWL.propertyDisjointWith, node2));
        }
        else {
            const bn = this.newBlankNode();
            this.quads.push(quad(bn, RDF.type, OWL.AllDisjointProperties));
            const listNode = this.serializeList(axiom.properties.map(p => this.serializeDataPropertyExpression(p)));
            this.quads.push(quad(bn, OWL.members, listNode));
        }
    }
    serializeDataPropertyDomain(axiom) {
        const propNode = this.serializeDataPropertyExpression(axiom.property);
        const domainNode = this.serializeClassExpression(axiom.domain);
        this.quads.push(quad(propNode, RDFS.domain, domainNode));
    }
    serializeDataPropertyRange(axiom) {
        const propNode = this.serializeDataPropertyExpression(axiom.property);
        const rangeNode = this.serializeDataRange(axiom.range);
        this.quads.push(quad(propNode, RDFS.range, rangeNode));
    }
    serializeFunctionalDataProperty(axiom) {
        const propNode = this.serializeDataPropertyExpression(axiom.property);
        this.quads.push(quad(propNode, RDF.type, OWL.FunctionalProperty));
    }
    // =========================================================================
    // Other Axiom Serialization
    // =========================================================================
    serializeDatatypeDefinition(axiom) {
        const dtNode = this.resolveIRI(axiom.datatype.iri);
        const rangeNode = this.serializeDataRange(axiom.dataRange);
        this.quads.push(quad(dtNode, OWL.equivalentClass, rangeNode));
    }
    serializeHasKey(axiom) {
        const classNode = this.serializeClassExpression(axiom.classExpression);
        const keyProps = [
            ...axiom.objectPropertyExpressions.map(p => this.serializeObjectPropertyExpression(p)),
            ...axiom.dataPropertyExpressions.map(p => this.serializeDataPropertyExpression(p))
        ];
        const listNode = this.serializeList(keyProps);
        this.quads.push(quad(classNode, OWL.hasKey, listNode));
    }
    // =========================================================================
    // Assertion Serialization
    // =========================================================================
    serializeSameIndividual(axiom) {
        const nodes = axiom.individuals.map(i => this.serializeIndividual(i));
        for (let i = 0; i < nodes.length - 1; i++) {
            this.quads.push(quad(nodes[i], OWL.sameAs, nodes[i + 1]));
        }
    }
    serializeDifferentIndividuals(axiom) {
        if (axiom.individuals.length === 2) {
            const node1 = this.serializeIndividual(axiom.individuals[0]);
            const node2 = this.serializeIndividual(axiom.individuals[1]);
            this.quads.push(quad(node1, OWL.differentFrom, node2));
        }
        else {
            const bn = this.newBlankNode();
            this.quads.push(quad(bn, RDF.type, OWL.AllDifferent));
            const listNode = this.serializeList(axiom.individuals.map(i => this.serializeIndividual(i)));
            this.quads.push(quad(bn, OWL.distinctMembers, listNode));
        }
    }
    serializeClassAssertion(axiom) {
        const indNode = this.serializeIndividual(axiom.individual);
        const classNode = this.serializeClassExpression(axiom.classExpression);
        this.quads.push(quad(indNode, RDF.type, classNode));
    }
    serializeObjectPropertyAssertion(axiom) {
        const sourceNode = this.serializeIndividual(axiom.sourceIndividual);
        const targetNode = this.serializeIndividual(axiom.targetIndividual);
        if (axiom.property.type === 'ObjectInverseOf') {
            // For inverse properties, swap source and target and use the underlying property
            const propNode = this.resolveIRI(axiom.property.property.iri);
            this.quads.push(quad(targetNode, propNode, sourceNode));
        }
        else {
            const propNode = this.resolveIRI(axiom.property.iri);
            this.quads.push(quad(sourceNode, propNode, targetNode));
        }
    }
    serializeNegativeObjectPropertyAssertion(axiom) {
        const bn = this.newBlankNode();
        this.quads.push(quad(bn, RDF.type, OWL.NegativePropertyAssertion));
        this.quads.push(quad(bn, OWL.sourceIndividual, this.serializeIndividual(axiom.sourceIndividual)));
        this.quads.push(quad(bn, OWL.assertionProperty, this.serializeObjectPropertyExpression(axiom.property)));
        this.quads.push(quad(bn, OWL.targetIndividual, this.serializeIndividual(axiom.targetIndividual)));
    }
    serializeDataPropertyAssertion(axiom) {
        const propNode = this.serializeDataPropertyExpression(axiom.property);
        const sourceNode = this.serializeIndividual(axiom.sourceIndividual);
        const valueNode = this.serializeLiteral(axiom.targetValue);
        this.quads.push(quad(sourceNode, propNode, valueNode));
    }
    serializeNegativeDataPropertyAssertion(axiom) {
        const bn = this.newBlankNode();
        this.quads.push(quad(bn, RDF.type, OWL.NegativePropertyAssertion));
        this.quads.push(quad(bn, OWL.sourceIndividual, this.serializeIndividual(axiom.sourceIndividual)));
        this.quads.push(quad(bn, OWL.assertionProperty, this.serializeDataPropertyExpression(axiom.property)));
        this.quads.push(quad(bn, OWL.targetValue, this.serializeLiteral(axiom.targetValue)));
    }
    // =========================================================================
    // Annotation Axiom Serialization
    // =========================================================================
    serializeAnnotationAssertion(axiom) {
        const propNode = this.resolveIRI(axiom.property.iri);
        let subjectNode;
        if ('kind' in axiom.subject) {
            subjectNode = this.resolveIRI(axiom.subject);
        }
        else if ('type' in axiom.subject && axiom.subject.type === 'AnonymousIndividual') {
            subjectNode = blankNode(axiom.subject.nodeId);
        }
        else {
            throw new Error('Unknown annotation subject type');
        }
        const valueNode = this.serializeAnnotationValue(axiom.value);
        this.quads.push(quad(subjectNode, propNode, valueNode));
    }
    serializeSubAnnotationPropertyOf(axiom) {
        const subNode = this.resolveIRI(axiom.subProperty.iri);
        const superNode = this.resolveIRI(axiom.superProperty.iri);
        this.quads.push(quad(subNode, RDFS.subPropertyOf, superNode));
    }
    serializeAnnotationPropertyDomain(axiom) {
        const propNode = this.resolveIRI(axiom.property.iri);
        const domainNode = this.resolveIRI(axiom.domain);
        this.quads.push(quad(propNode, RDFS.domain, domainNode));
    }
    serializeAnnotationPropertyRange(axiom) {
        const propNode = this.resolveIRI(axiom.property.iri);
        const rangeNode = this.resolveIRI(axiom.range);
        this.quads.push(quad(propNode, RDFS.range, rangeNode));
    }
    // =========================================================================
    // Class Expression Serialization
    // =========================================================================
    serializeClassExpression(ce) {
        switch (ce.type) {
            case 'Class':
                return this.resolveIRI(ce.iri);
            case 'ObjectIntersectionOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Class));
                const listNode = this.serializeList(ce.classExpressions.map(c => this.serializeClassExpression(c)));
                this.quads.push(quad(bn, OWL.intersectionOf, listNode));
                return bn;
            }
            case 'ObjectUnionOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Class));
                const listNode = this.serializeList(ce.classExpressions.map(c => this.serializeClassExpression(c)));
                this.quads.push(quad(bn, OWL.unionOf, listNode));
                return bn;
            }
            case 'ObjectComplementOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Class));
                this.quads.push(quad(bn, OWL.complementOf, this.serializeClassExpression(ce.classExpression)));
                return bn;
            }
            case 'ObjectOneOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Class));
                const listNode = this.serializeList(ce.individuals.map(i => this.serializeIndividual(i)));
                this.quads.push(quad(bn, OWL.oneOf, listNode));
                return bn;
            }
            case 'ObjectSomeValuesFrom': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                this.quads.push(quad(bn, OWL.someValuesFrom, this.serializeClassExpression(ce.filler)));
                return bn;
            }
            case 'ObjectAllValuesFrom': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                this.quads.push(quad(bn, OWL.allValuesFrom, this.serializeClassExpression(ce.filler)));
                return bn;
            }
            case 'ObjectHasValue': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                this.quads.push(quad(bn, OWL.hasValue, this.serializeIndividual(ce.individual)));
                return bn;
            }
            case 'ObjectHasSelf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                this.quads.push(quad(bn, OWL.hasSelf, literal('true', namedNode(`${XSD_NAMESPACE}boolean`))));
                return bn;
            }
            case 'ObjectMinCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                if (ce.filler) {
                    this.quads.push(quad(bn, OWL.minQualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onClass, this.serializeClassExpression(ce.filler)));
                }
                else {
                    this.quads.push(quad(bn, OWL.minCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            case 'ObjectMaxCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                if (ce.filler) {
                    this.quads.push(quad(bn, OWL.maxQualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onClass, this.serializeClassExpression(ce.filler)));
                }
                else {
                    this.quads.push(quad(bn, OWL.maxCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            case 'ObjectExactCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeObjectPropertyExpression(ce.property)));
                if (ce.filler) {
                    this.quads.push(quad(bn, OWL.qualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onClass, this.serializeClassExpression(ce.filler)));
                }
                else {
                    this.quads.push(quad(bn, OWL.cardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            case 'DataSomeValuesFrom': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                // Use first property for simple case (n-ary would need different handling)
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.properties[0])));
                this.quads.push(quad(bn, OWL.someValuesFrom, this.serializeDataRange(ce.dataRange)));
                return bn;
            }
            case 'DataAllValuesFrom': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                // Use first property for simple case (n-ary would need different handling)
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.properties[0])));
                this.quads.push(quad(bn, OWL.allValuesFrom, this.serializeDataRange(ce.dataRange)));
                return bn;
            }
            case 'DataHasValue': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.property)));
                this.quads.push(quad(bn, OWL.hasValue, this.serializeLiteral(ce.literal)));
                return bn;
            }
            case 'DataMinCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.property)));
                if (ce.dataRange) {
                    this.quads.push(quad(bn, OWL.minQualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onDataRange, this.serializeDataRange(ce.dataRange)));
                }
                else {
                    this.quads.push(quad(bn, OWL.minCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            case 'DataMaxCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.property)));
                if (ce.dataRange) {
                    this.quads.push(quad(bn, OWL.maxQualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onDataRange, this.serializeDataRange(ce.dataRange)));
                }
                else {
                    this.quads.push(quad(bn, OWL.maxCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            case 'DataExactCardinality': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, OWL.Restriction));
                this.quads.push(quad(bn, OWL.onProperty, this.serializeDataPropertyExpression(ce.property)));
                if (ce.dataRange) {
                    this.quads.push(quad(bn, OWL.qualifiedCardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                    this.quads.push(quad(bn, OWL.onDataRange, this.serializeDataRange(ce.dataRange)));
                }
                else {
                    this.quads.push(quad(bn, OWL.cardinality, literal(ce.cardinality.toString(), namedNode(`${XSD_NAMESPACE}nonNegativeInteger`))));
                }
                return bn;
            }
            default:
                throw new Error(`Unknown class expression type: ${ce.type}`);
        }
    }
    // =========================================================================
    // Data Range Serialization
    // =========================================================================
    serializeDataRange(dr) {
        switch (dr.type) {
            case 'Datatype':
                return this.resolveIRI(dr.iri);
            case 'DataIntersectionOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                const listNode = this.serializeList(dr.dataRanges.map(d => this.serializeDataRange(d)));
                this.quads.push(quad(bn, OWL.intersectionOf, listNode));
                return bn;
            }
            case 'DataUnionOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                const listNode = this.serializeList(dr.dataRanges.map(d => this.serializeDataRange(d)));
                this.quads.push(quad(bn, OWL.unionOf, listNode));
                return bn;
            }
            case 'DataComplementOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                this.quads.push(quad(bn, OWL.datatypeComplementOf, this.serializeDataRange(dr.dataRange)));
                return bn;
            }
            case 'DataOneOf': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                const listNode = this.serializeList(dr.literals.map(l => this.serializeLiteral(l)));
                this.quads.push(quad(bn, OWL.oneOf, listNode));
                return bn;
            }
            case 'DatatypeRestriction': {
                const bn = this.newBlankNode();
                this.quads.push(quad(bn, RDF.type, namedNode(`${RDFS_NAMESPACE}Datatype`)));
                this.quads.push(quad(bn, OWL.onDatatype, this.resolveIRI(dr.datatype.iri)));
                const restrictionList = dr.facetRestrictions.map(fr => {
                    const frBn = this.newBlankNode();
                    this.quads.push(quad(frBn, this.resolveIRI(fr.facet), this.serializeLiteral(fr.value)));
                    return frBn;
                });
                this.quads.push(quad(bn, OWL.withRestrictions, this.serializeList(restrictionList)));
                return bn;
            }
            default:
                throw new Error(`Unknown data range type: ${dr.type}`);
        }
    }
    // =========================================================================
    // Property Expression Serialization
    // =========================================================================
    serializeObjectPropertyExpression(pe) {
        if (pe.type === 'ObjectProperty') {
            return this.resolveIRI(pe.iri);
        }
        // ObjectInverseOf
        const bn = this.newBlankNode();
        this.quads.push(quad(bn, OWL.inverseOf, this.resolveIRI(pe.property.iri)));
        return bn;
    }
    serializeDataPropertyExpression(pe) {
        return this.resolveIRI(pe.iri);
    }
    // =========================================================================
    // Individual Serialization
    // =========================================================================
    serializeIndividual(ind) {
        if (ind.type === 'NamedIndividual') {
            return this.resolveIRI(ind.iri);
        }
        // AnonymousIndividual
        return blankNode(ind.nodeId);
    }
    // =========================================================================
    // RDF List Serialization
    // =========================================================================
    serializeList(items) {
        if (items.length === 0) {
            return RDF.nil;
        }
        const listNodes = items.map(() => this.newBlankNode());
        for (let i = 0; i < items.length; i++) {
            this.quads.push(quad(listNodes[i], RDF.first, items[i]));
            if (i < items.length - 1) {
                this.quads.push(quad(listNodes[i], RDF.rest, listNodes[i + 1]));
            }
            else {
                this.quads.push(quad(listNodes[i], RDF.rest, RDF.nil));
            }
        }
        return listNodes[0];
    }
}
/**
 * Convenience function to serialize Ontology to Turtle string.
 */
export function serializeTurtle(ontology) {
    const serializer = new Owl2TurtleSerializer();
    return serializer.serialize(ontology);
}
